import React, { useEffect, useState } from "react";
import { relevanceFromDistance } from "../utils";
import { SearchIcon } from "./Icons";
import { Pagination } from "./Pagination";

// Simple inline icons to replace missing imports
const ExternalLinkIcon = () => (
	<svg
		width="12"
		height="12"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
	>
		<path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3" />
	</svg>
);

const InfoIcon = () => (
	<svg
		width="12"
		height="12"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
	>
		<circle cx="12" cy="12" r="10" />
		<text
			x="12"
			y="16"
			textAnchor="middle"
			fontSize="14"
			fill="currentColor"
			fontWeight="bold"
		>
			i
		</text>
	</svg>
);

const DownloadIcon = () => (
	<svg
		width="16"
		height="16"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
	>
		<path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />
	</svg>
);

const ChevronDownIcon = () => (
	<svg
		width="12"
		height="12"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
	>
		<polyline points="6,9 12,15 18,9" />
	</svg>
);

const generateUniqueAppKey = (item, index, currentPage) => {
	const appName = item.app_name || item.name || "unknown";
	const platform = item.platform || item.market_source || "unknown";
	const bundleId = item.bundle_id || "";
	const itemId = item.id || "";

	const compositeKey = `${appName}-${platform}-${bundleId}-${itemId}-${currentPage}-${index}`;
	return compositeKey.replace(/[^a-zA-Z0-9-_]/g, "_");
};

export const AppResultsList = ({
	data,
	selectedResults,
	onResultSelect,
	currentPage,
	totalPages,
	totalItems,
	itemsPerPage,
	onPageChange,
	onSelectAll,
	isAllSelected,
	isExporting,
	onExport,
	searchTerm,
	onSearchChange,
	searchResults,
	isLoading = false,
	error = null,
	// Sort props - CHANGED: Default to impressions descending
	sortBy = "totalImpressions",
	sortOrder = "desc",
	onSortByChange,
	onSortOrderChange,
	// Full dataset for proper sorting
	fullDataset = [],
}) => {
	const [activeTooltip, setActiveTooltip] = useState(null);
	const [tooltipPosition, setTooltipPosition] = useState({
		top: true,
		left: 0,
	});
	const [isSearchVisible, setIsSearchVisible] = useState(false);
	const [isExportDropdownOpen, setIsExportDropdownOpen] = useState(false);

	// Custom CTR calculation function
	const calculateCTR = (item) => {
		if (
			item.totalCprClicks &&
			item.totalImpressionsWithClickMacro &&
			item.totalImpressionsWithClickMacro > 0
		) {
			return item.totalCprClicks / item.totalImpressionsWithClickMacro;
		}
		// Fallback to existing calculated CTR if available
		return item.calculated_ctr || item.ctr || 0;
	};

	const calculateViewability = (item) => {
		if (
			item.totalViewables &&
			item.totalViewabilityMeasures &&
			item.totalViewabilityMeasures > 0
		) {
			return Math.round(
				(item.totalViewables / item.totalViewabilityMeasures) * 100,
			);
		}
		return item.avgViewability || item.viewability || 0; // Fallback
	};

	//composite score
	const calculateCScore = (item) => {
		if (item.compositeScore) {
			return item.compositeScore;
		} else {
			return item._original.compositeScore;
		}
	};

	const calculateVTR = (item) => {
		if (
			item.totalCompleteFirstOccurrence &&
			item.totalStartFirstOccurrence &&
			item.totalStartFirstOccurrence > 0
		) {
			return parseFloat(
				(
					(item.totalCompleteFirstOccurrence / item.totalStartFirstOccurrence) *
					100
				).toFixed(4),
			);
		}
		return item.avgVtr || item.vtr || 0; // Fallback
	};

	// ADD: Market requests calculation
	const calculateMarketRequests = (item) => {
		// Check both the main item and _original object
		const rawValue =
			item.totalCprMarketRequests ||
			item._original?.totalCprMarketRequests ||
			item.marketRequests;

		if (!rawValue || rawValue === "n/a") {
			return "N/A";
		}

		const numericValue = parseFloat(rawValue);

		if (isNaN(numericValue)) {
			return "N/A";
		}

		// Format large numbers with commas
		return Math.round(numericValue).toLocaleString();
	};

	// Replace the existing calculateRelevance function in AppResultsList.jsx with this debug version

	const calculateRelevance = (item) => {
		// Deep dive into _original object if it exists
		if (item._original) {
			// Look for score in various formats
			const possibleScoreFields = [
				"score",
				"relevance",
				"relevanceScore",
				"performance_score",
				"search_score",
				"composite_score",
				"rating_score",
			];
		}
		// Try the original working formula - CHECK _original object first
		const appScore = item?.score || item?._original?.score;

		if (
			appScore !== undefined &&
			appScore !== null &&
			!isNaN(parseFloat(appScore))
		) {
			const score = parseFloat(appScore);

			console.groupEnd();
			return Math.round(relevanceFromDistance(score, "APPS"));
		}
		// Fallback
		console.log("❌ No valid score found, using fallback value 0");
		console.groupEnd();
		return 0;
	};

	// Get relevance class for color coding
	const getRelevanceClass = (relevance) => {
		if (relevance >= 85) return "excellent";
		if (relevance >= 78) return "great";
		if (relevance >= 65) return "acceptable";
		if (relevance >= 57) return "poor";
		return "irrelevant";
	};

	// Close tooltip when clicking outside
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (
				!event.target.closest(".info-icon-wrapper") &&
				!event.target.closest(".metrics-tooltip")
			) {
				setActiveTooltip(null);
			}
		};

		if (activeTooltip) {
			document.addEventListener("click", handleClickOutside);
		}

		return () => {
			document.removeEventListener("click", handleClickOutside);
		};
	}, [activeTooltip]);

	// Close export dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (!event.target.closest(".export-dropdown")) {
				setIsExportDropdownOpen(false);
			}
		};

		if (isExportDropdownOpen) {
			document.addEventListener("click", handleClickOutside);
		}

		return () => {
			document.removeEventListener("click", handleClickOutside);
		};
	}, [isExportDropdownOpen]);

	const getRawMarketRequests = (item) => {
		const rawValue =
			item.totalCprMarketRequests ||
			item._original?.totalCprMarketRequests ||
			item.marketRequests;

		if (!rawValue || rawValue === "n/a") {
			return 0;
		}

		const numericValue = parseFloat(rawValue);
		return isNaN(numericValue) ? 0 : numericValue;
	};

	// Sort the full dataset before pagination
	const getSortedData = (dataToSort) => {
		if (!Array.isArray(dataToSort) || dataToSort.length === 0) return [];

		return [...dataToSort].sort((a, b) => {
			let aValue, bValue;

			switch (sortBy) {
				case "relevance": {
					// Use calculated relevance or fallback to score/relevance field
					const aScoreValue =
						a.score ||
						a._original?.score ||
						a.relevance ||
						a._original?.relevance ||
						a.rating / 5;
					const bScoreValue =
						b.score ||
						b._original?.score ||
						b.relevance ||
						b._original?.relevance ||
						b.rating / 5;
					aValue = calculateRelevance(a) || 0;
					bValue = calculateRelevance(b) || 0;
					break;
				}
				case "totalImpressions":
				case "impressions":
					aValue = a.totalImpressions || a.impressions || 0;
					bValue = b.totalImpressions || b.impressions || 0;
					break;
				case "marketRequests":
					aValue = getRawMarketRequests(a);
					bValue = getRawMarketRequests(b);
					break;
				case "rating":
					aValue = parseFloat(a.rating) || 0;
					bValue = parseFloat(b.rating) || 0;
					break;
				case "downloads":
					aValue = parseFloat(a.downloads) || 0;
					bValue = parseFloat(b.downloads) || 0;
					break;
				case "viewability":
					aValue = calculateViewability(a);
					bValue = calculateViewability(b);
					break;
				case "ctr":
					aValue = calculateCTR(a) || 0;
					bValue = calculateCTR(b) || 0;
					break;
				case "vtr":
					aValue = calculateVTR(a) || 0;
					bValue = calculateVTR(b) || 0;
					break;
				case "compositeScore":
					aValue = calculateCScore(a) || 0;
					bValue = calculateCScore(b) || 0;
					break;
				case "name":
					aValue = a.app_name || a.name || "";
					bValue = b.app_name || b.name || "";
					return sortOrder === "asc"
						? aValue.localeCompare(bValue)
						: bValue.localeCompare(aValue);
				default:
					return 0;
			}

			// Ensure we're comparing numbers
			aValue = Number(aValue) || 0;
			bValue = Number(bValue) || 0;

			return sortOrder === "asc" ? aValue - bValue : bValue - aValue;
		});
	};

	// Apply search filter and sorting to full dataset
	const getFilteredAndSortedData = () => {
		const datasetToUse = fullDataset.length > 0 ? fullDataset : data;

		let filteredData = datasetToUse;

		// Apply search filter
		if (searchTerm && searchTerm.trim()) {
			filteredData = filteredData.filter(
				(item) =>
					(item.app_name || item.name || "")
						.toLowerCase()
						.includes(searchTerm.toLowerCase()) ||
					(item.developer || item.creator || "")
						.toLowerCase()
						.includes(searchTerm.toLowerCase()) ||
					(item.category || item.subcategory || "")
						.toLowerCase()
						.includes(searchTerm.toLowerCase()) ||
					(item.bundle_id || "")
						.toLowerCase()
						.includes(searchTerm.toLowerCase()),
			);
		}

		// Apply sorting
		return getSortedData(filteredData);
	};

	// Get paginated data from sorted/filtered dataset
	const getPaginatedData = () => {
		const sortedFilteredData = getFilteredAndSortedData();
		const startIndex = (currentPage - 1) * itemsPerPage;
		const endIndex = startIndex + itemsPerPage;
		return sortedFilteredData.slice(startIndex, endIndex);
	};

	// Calculate total pages based on filtered data
	const getCalculatedTotalPages = () => {
		const filteredData = getFilteredAndSortedData();
		return Math.ceil(filteredData.length / itemsPerPage);
	};

	// Calculate total items based on filtered data
	const getCalculatedTotalItems = () => {
		const filteredData = getFilteredAndSortedData();
		return filteredData.length;
	};

	// FIXED: Improved tooltip position calculation
	const calculateTooltipPosition = (buttonElement) => {
		if (!buttonElement)
			return {
				top: true,
				left: 100,
				right: "auto",
				buttonTop: 0,
				buttonBottom: 100,
			};

		const rect = buttonElement.getBoundingClientRect();
		const viewportHeight = window.innerHeight;
		const viewportWidth = window.innerWidth;
		const tooltipHeight = 400;
		const tooltipWidth = 350;

		// Calculate if tooltip should show above or below
		const spaceBelow = viewportHeight - rect.bottom;
		const spaceAbove = rect.top;
		const showAbove = spaceBelow < tooltipHeight && spaceAbove > tooltipHeight;

		// Calculate horizontal position
		let left = rect.left + rect.width / 2 - tooltipWidth / 2;

		// Adjust horizontal position if tooltip would go off-screen
		if (left < 10) {
			left = 10;
		} else if (left + tooltipWidth > viewportWidth - 10) {
			left = viewportWidth - tooltipWidth - 10;
		}

		return {
			top: !showAbove,
			left: Math.max(10, left),
			right: "auto",
			buttonTop: rect.top,
			buttonBottom: rect.bottom,
			showAbove,
		};
	};

	// Handle select all functionality
	const handleSelectAll = () => {
		if (onSelectAll) {
			onSelectAll(!isAllSelected);
		}
	};

	// Handle app search
	const handleAppSearch = (event) => {
		if (onSearchChange) {
			onSearchChange(event.target.value);
		}
	};

	// Clear search
	const clearSearch = () => {
		if (onSearchChange) {
			onSearchChange("");
			setIsSearchVisible(false);
		}
	};

	// Handle export functions
	const handleExportSummarized = () => {
		if (onExport) {
			onExport("summarized");
		}
		setIsExportDropdownOpen(false);
	};

	const handleExportDetailed = () => {
		if (onExport) {
			onExport("detailed");
		}
		setIsExportDropdownOpen(false);
	};

	// Handle sort changes
	const handleSortByChange = (newSortBy) => {
		if (onSortByChange) {
			onSortByChange(newSortBy);
		}
	};

	const handleSortOrderToggle = () => {
		if (onSortOrderChange) {
			onSortOrderChange(sortOrder === "asc" ? "desc" : "asc");
		}
	};

	// Format number values for display
	const formatNumber = (value) => {
		if (!value || value === "N/A") return "N/A";
		const num = typeof value === "string" ? parseFloat(value) : value;
		if (isNaN(num)) return "N/A";

		if (num >= 1000000000) {
			return (num / 1000000000).toFixed(2) + "B";
		} else if (num >= 1000000) {
			return (num / 1000000).toFixed(2) + "M";
		}
		return num.toLocaleString();
	};

	// Format percentage values
	const formatPercentage = (value) => {
		if (!value || value === "N/A") return "0.00%";
		const num = typeof value === "string" ? parseFloat(value) : value;
		if (isNaN(num)) return "N/A";
		return `${num.toFixed(2)}%`;
	};

	// Format relevance score
	const formatRelevance = (value) => {
		if (!value && value !== 0) return "N/A";
		const num = typeof value === "string" ? parseFloat(value) : value;
		if (isNaN(num)) return "N/A";
		return num.toFixed(1);
	};

	// Format currency values
	const formatCurrency = (value) => {
		if (!value || value === "N/A") return "N/A";
		const num = typeof value === "string" ? parseFloat(value) : value;
		if (isNaN(num)) return "N/A";

		if (num >= 1000) {
			return `$${(num / 1000).toFixed(1)}K`;
		}
		return `$${num.toFixed(4)}`;
	};

	// Format rating
	const formatRating = (rating) => {
		if (!rating || rating === 0 || rating === "N/A") return null;
		const num = typeof rating === "string" ? parseFloat(rating) : rating;
		if (isNaN(num)) return null;
		return num.toFixed(1);
	};

	// Format date
	const formatDate = (dateString) => {
		if (!dateString) return "N/A";
		try {
			return new Date(dateString).toLocaleDateString();
		} catch {
			return "N/A";
		}
	};

	// Get app store URL (if available)
	const getAppStoreUrl = (item) => {
		if (item.app_store_url) return item.app_store_url;
		if (item.bundle_id && item.platform) {
			const platform = item.platform.toLowerCase();
			if (platform.includes("android")) {
				return `https://play.google.com/store/apps/details?id=${item.bundle_id}`;
			} else if (platform.includes("ios")) {
				return `https://apps.apple.com/app/id${item.bundle_id}`;
			}
		}
		return null;
	};

	const prepareSummarizedData = (items) => {
		return items.map((item) => {
			const relevance = calculateRelevance(item);
			return {
				"App Name": item.app_name || item.name || "Unknown App",
				"Bundle ID": item.bundle_id || "N/A",
				Category: item.category || item.subcategory || "N/A",
				Developer: item.developer || item.creator || "N/A",
				Downloads: formatNumber(item.downloads),
				Rating: formatRating(item.rating) || "N/A",
				"Rating Count": formatNumber(item.rating_count),
				Relevance: formatRelevance(relevance),
				"Market Requests": formatNumber(calculateMarketRequests(item)),
				Viewability: formatPercentage(calculateViewability(item)),
				CTR: formatPercentage(calculateCTR(item)),
				VTR: formatPercentage(calculateVTR(item)),
				Impressions: formatNumber(item.totalImpressions || item.impressions),
			};
		});
	};

	const prepareDetailedData = (items) => {
		return items.map((item) => {
			const scoreValue = item.score;
			const relevance = calculateRelevance(item);
			return {
				// Basic App Information
				"App Name": item.app_name || item.name || "Unknown App",
				"Bundle ID": item.bundle_id || "N/A",
				Developer: item.developer || item.creator || "N/A",
				Category: item.category || item.subcategory || "N/A",
				"Content Rating": item.content_rating || "N/A",
				"Market Source": item.market_source || "N/A",
				Platform: item.platform || "N/A",

				// App Store Metrics
				Downloads: formatNumber(item.downloads),
				Rating: formatRating(item.rating) || "N/A",
				"Rating Count": formatNumber(item.rating_count),
				"Release Date": formatDate(item.release_date),
				"Last Update": formatDate(item.update_date),

				// Performance Metrics
				"Relevance Score": formatRelevance(relevance),
				"Market Requests": formatNumber(calculateMarketRequests(item)),
				"Total Impressions": formatNumber(
					item.totalImpressions || item.impressions,
				),
				"Total Clicks": formatNumber(item.totalCprClicks || item.clicks),
				CTR: formatPercentage(calculateCTR(item)),
				Viewability: formatPercentage(calculateViewability(item)),
				VTR: formatPercentage(calculateVTR(item)),
				CPM: formatCurrency(item.avgBidCpm / 1000000 || item.cpm),
				"Revenue Per Impression": formatCurrency(
					item.calculated_revenueperimpression || item.rpi,
				),
				"Cost Per Impression": formatCurrency(
					item.calculated_costperimpression || item.cpi,
				),

				// Additional Information
				Description: item.short_description || item.description || "N/A",
				"App Store URL": getAppStoreUrl(item) || "N/A",
				Languages: Array.isArray(item.languages)
					? item.languages.join(", ")
					: item.languages || "N/A",
				Keywords: Array.isArray(item.keywords)
					? item.keywords.join(", ")
					: item.keywords || "N/A",
				Domain: item.domain || item.app_domain || "N/A",

				// Raw Values (for data analysis)
				"Rating (Raw)": item.rating || "N/A",
				"Rating Count (Raw)": item.rating_count || "N/A",
				"Downloads (Raw)": item.downloads || "N/A",
				"Relevance (Raw)": relevance || "N/A",
				"Market Requests (Raw)": calculateMarketRequests(item) || "N/A",
				"Impressions (Raw)": item.totalImpressions || item.impressions || "N/A",
				"Clicks (Raw)": item.totalCprClicks || item.clicks || "N/A",
				"CTR (Raw)": calculateCTR(item) || "N/A",
				"CTR (Original)": item.calculated_ctr || item.ctr || "N/A",
				"Viewability (Raw)": calculateViewability(item) || "N/A",
				"VTR (Raw)": calculateVTR(item) || "N/A",
			};
		});
	};

	// Handle tooltip click toggle
	const handleTooltipToggle = (itemId, event) => {
		event.stopPropagation();
		if (activeTooltip === itemId) {
			setActiveTooltip(null);
		} else {
			const buttonElement =
				event.currentTarget.querySelector(".info-icon") || event.currentTarget;
			const position = calculateTooltipPosition(buttonElement);
			setTooltipPosition(position);
			setActiveTooltip(itemId);
		}
	};

	// Export Dropdown Component
	const ExportDropdown = () => (
		<div className="export-dropdown">
			<button
				onClick={() => setIsExportDropdownOpen(!isExportDropdownOpen)}
				className="btn-medium-secondary export-dropdown-btn"
				disabled={isExporting || (data && data.length === 0)}
			>
				<DownloadIcon />
				{isExporting ? "Exporting..." : "Export Results"}
				<ChevronDownIcon />
			</button>

			{isExportDropdownOpen && (
				<div className="dropdown-menu">
					<button
						onClick={handleExportSummarized}
						className="dropdown-item"
						disabled={isExporting === "summarized"}
					>
						Export Summarized
					</button>
					<button
						onClick={handleExportDetailed}
						className="dropdown-item"
						disabled={isExporting === "detailed"}
					>
						Export Detailed
					</button>
				</div>
			)}
		</div>
	);

	// FIXED: Improved tooltip component with better positioning and z-index
	const AppMetricsTooltip = ({ item, isVisible, position }) => {
		const getFieldValue = (fieldName, fallbackField = null) => {
			const original = item._original || {};
			const normalized = item || {};

			// Try exact field name first
			if (original[fieldName] !== undefined && original[fieldName] !== null) {
				return original[fieldName];
			}
			if (
				normalized[fieldName] !== undefined &&
				normalized[fieldName] !== null
			) {
				return normalized[fieldName];
			}

			// Try fallback field
			if (fallbackField) {
				if (
					original[fallbackField] !== undefined &&
					original[fallbackField] !== null
				) {
					return original[fallbackField];
				}
				if (
					normalized[fallbackField] !== undefined &&
					normalized[fallbackField] !== null
				) {
					return normalized[fallbackField];
				}
			}

			return "N/A";
		};

		// Call debug function
		//debugAllFields();

		// Helper to get array field values
		const getArrayField = (fieldName) => {
			const value = getFieldValue(fieldName);
			if (value === "N/A" || value === null || value === undefined) return [];
			if (Array.isArray(value)) return value;
			if (typeof value === "string") {
				// Handle JSON string arrays like '["Games","Casual","Puzzle"]'
				if (value.startsWith("[") && value.endsWith("]")) {
					try {
						return JSON.parse(value);
					} catch {
						return [value];
					}
				}
				return [value];
			}
			return [value];
		};

		const tooltipStyle = {
			position: "fixed",
			left: `${position.left}px`,
			zIndex: 999999,
			pointerEvents: "auto",
		};

		// Set top or bottom position based on calculation
		if (position.showAbove) {
			tooltipStyle.bottom = `${window.innerHeight - position.buttonTop + 10}px`;
		} else {
			tooltipStyle.top = `${position.buttonBottom + 10}px`;
		}

		const scoreValue = getFieldValue("score");
		const relevance = calculateRelevance(item);

		// Helper functions for formatting
		const formatLargeNumber = (value) => {
			if (!value || value === "N/A" || isNaN(value)) return "N/A";
			const num = typeof value === "string" ? parseFloat(value) : value;
			if (isNaN(num)) return "N/A";

			if (num >= 1000000000) {
				return `${(num / 1000000000).toFixed(2)}B`;
			} else if (num >= 1000000) {
				return `${(num / 1000000).toFixed(2)}M`;
			} else if (num >= 1000) {
				return `${(num / 1000).toFixed(1)}K`;
			}
			return num.toLocaleString();
		};

		const formatCurrency = (value, divideBy1M = false) => {
			if (!value || value === "N/A" || isNaN(value)) return "N/A";
			const num = typeof value === "string" ? parseFloat(value) : value;
			if (isNaN(num)) return "N/A";

			const adjustedNum = divideBy1M ? num / 1000000 : num;

			if (adjustedNum >= 1000000) {
				return `$${(adjustedNum / 1000000).toFixed(2)}M`;
			} else if (adjustedNum >= 1000) {
				return `$${(adjustedNum / 1000).toFixed(1)}K`;
			}
			return `$${adjustedNum.toFixed(adjustedNum < 1 ? 4 : 2)}`;
		};

		const formatPercentage = (value, decimals = 2) => {
			if (!value && value !== 0) return "N/A";
			const num = typeof value === "string" ? parseFloat(value) : value;
			if (isNaN(num)) return "N/A";
			return `${num.toFixed(decimals)}%`;
		};

		const formatArray = (arr) => {
			if (!arr || !Array.isArray(arr) || arr.length === 0) return "N/A";
			return arr.join(", ");
		};

		const formatBoolean = (value) => {
			if (value === null || value === undefined) return "No";
			return value ? "Yes" : "No";
		};

		return (
			<div className="metrics-tooltip enhanced-tooltip" style={tooltipStyle}>
				<div className="tooltip-header">
					<h4>{getFieldValue("app_name", "name") || "Unknown App"}</h4>
					<div className="header-badges">
						{getFieldValue("rating") && (
							<span className="rating-badge-tooltip">
								★ {formatRating(getFieldValue("rating"))}
							</span>
						)}
						<span
							className={`relevance-badge-tooltip ${getRelevanceClass(relevance)}`}
						>
							{formatRelevance(relevance)}% Relevance
						</span>
					</div>
				</div>

				<div className="tooltip-content">
					{/* App Basic Information */}
					<div className="tooltip-section">
						<h5>App Information</h5>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">App ID:</span>
							<span className="tooltip-metric-value">
								{getFieldValue("app_id")}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">Bundle ID:</span>
							<span className="tooltip-metric-value">
								{getFieldValue("bundle_id")}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">Developer:</span>
							<span className="tooltip-metric-value">
								{getFieldValue("developer", "creator")}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">Category:</span>
							<span className="tooltip-metric-value">
								{getFieldValue("category")}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">Subcategory:</span>
							<span className="tooltip-metric-value">
								{formatArray(getArrayField("subcategory"))}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">Platform:</span>
							<span className="tooltip-metric-value">
								{getFieldValue("platform")}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">Domain:</span>
							<span className="tooltip-metric-value">
								{getFieldValue("domain")}
							</span>
						</div>
					</div>

					{/* App Store Information */}
					<div className="tooltip-section">
						<h5>App Store Details</h5>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">Rating:</span>
							<span className="tooltip-metric-value">
								{getFieldValue("rating") !== "N/A"
									? `${formatRating(getFieldValue("rating"))} ★`
									: "N/A"}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">Rating Count:</span>
							<span className="tooltip-metric-value">
								{formatLargeNumber(getFieldValue("rating_count"))}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">Downloads:</span>
							<span className="tooltip-metric-value">
								{formatLargeNumber(getFieldValue("downloads"))}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">Content Rating:</span>
							<span className="tooltip-metric-value">
								{getFieldValue("content_rating", "appinfo_content_rating")}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">Market Source:</span>
							<span className="tooltip-metric-value">
								{getFieldValue("market_source")}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">Market Status:</span>
							<span className="tooltip-metric-value">
								{getFieldValue("market_status")}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">Paid App:</span>
							<span className="tooltip-metric-value">
								{formatBoolean(getFieldValue("paid"))}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">COPPA Compliant:</span>
							<span className="tooltip-metric-value">
								{formatBoolean(getFieldValue("coppa"))}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">Release Date:</span>
							<span className="tooltip-metric-value">
								{formatDate(getFieldValue("release_date"))}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">Last Update:</span>
							<span className="tooltip-metric-value">
								{formatDate(getFieldValue("update_date"))}
							</span>
						</div>
					</div>

					{/* Targeting Information */}
					<div className="tooltip-section">
						<h5>Targeting Capabilities</h5>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">Media Subtypes:</span>
							<span className="tooltip-metric-value">
								{formatArray(getArrayField("iabmediasubtype"))}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">Device Types:</span>
							<span className="tooltip-metric-value">
								{formatArray(getArrayField("iabdevicetype"))}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">Video Placement:</span>
							<span className="tooltip-metric-value">
								{formatArray(getArrayField("videoplcmt"))}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">Geography:</span>
							<span className="tooltip-metric-value">
								{(() => {
									const geo = getArrayField("geography");
									return geo.length > 0
										? `${geo.length} countries${geo.length <= 5 ? ": " + geo.join(", ") : ""}`
										: "N/A";
								})()}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">Direct Publisher:</span>
							<span className="tooltip-metric-value">
								{formatBoolean(getArrayField("isDirect")[0])}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">Domain Reach:</span>
							<span className="tooltip-metric-value">
								{getFieldValue("domainReach")}
							</span>
						</div>
					</div>

					{/* Core Performance Metrics */}
					<div className="tooltip-section">
						<h5>Core Performance</h5>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">Relevance Score:</span>
							<span
								className={`tooltip-metric-value score ${getRelevanceClass(relevance)}`}
							>
								{formatRelevance(relevance)}%
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">Raw Score:</span>
							<span className="tooltip-metric-value">
								{getFieldValue("score")}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">Market Requests:</span>
							<span className="tooltip-metric-value">
								{formatLargeNumber(getFieldValue("totalCprMarketRequests"))}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">All Requests:</span>
							<span className="tooltip-metric-value">
								{formatLargeNumber(getFieldValue("totalCprAllRequests"))}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">Total Impressions:</span>
							<span className="tooltip-metric-value">
								{formatLargeNumber(getFieldValue("totalImpressions"))}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">
								Private Market Impressions:
							</span>
							<span className="tooltip-metric-value">
								{formatLargeNumber(
									getFieldValue("totalCprPrivateMarketImpressions"),
								)}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">
								Impressions w/ Click Macro:
							</span>
							<span className="tooltip-metric-value">
								{formatLargeNumber(
									getFieldValue("totalImpressionsWithClickMacro"),
								)}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">Total Clicks:</span>
							<span className="tooltip-metric-value">
								{formatLargeNumber(getFieldValue("totalCprClicks"))}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">CTR (Calculated):</span>
							<span className="tooltip-metric-value">
								{formatPercentage(getFieldValue("calculated_ctr") * 100, 4)}
							</span>
						</div>
					</div>

					{/* Video & Viewability Metrics */}
					<div className="tooltip-section">
						<h5>Video & Viewability</h5>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">Video Starts:</span>
							<span className="tooltip-metric-value">
								{formatLargeNumber(getFieldValue("totalStartFirstOccurrence"))}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">Video Completions:</span>
							<span className="tooltip-metric-value">
								{formatLargeNumber(
									getFieldValue("totalCompleteFirstOccurrence"),
								)}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">VTR (Avg):</span>
							<span className="tooltip-metric-value">
								{formatPercentage(getFieldValue("avgVtr"))}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">Viewables:</span>
							<span className="tooltip-metric-value">
								{formatLargeNumber(getFieldValue("totalViewables"))}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">
								Viewability Measures:
							</span>
							<span className="tooltip-metric-value">
								{formatLargeNumber(getFieldValue("totalViewabilityMeasures"))}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">Viewability (Avg):</span>
							<span className="tooltip-metric-value">
								{formatPercentage(getFieldValue("avgViewability"))}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">Viewability (Live):</span>
							<span className="tooltip-metric-value">
								{formatPercentage(calculateViewability(item))}
							</span>
						</div>
					</div>

					{/* Financial Performance - FIXED with correct field names */}
					<div className="tooltip-section">
						<h5>Financial Performance</h5>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">Total Fills:</span>
							<span className="tooltip-metric-value">
								{formatLargeNumber(getFieldValue("totalFills"))}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">Non-Zero Bids:</span>
							<span className="tooltip-metric-value">
								{formatLargeNumber(getFieldValue("totalNonZeroBids"))}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">Avg Bid CPM:</span>
							<span className="tooltip-metric-value">
								{formatCurrency(getFieldValue("avgBidCpm"), true)}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">Non-Zero Bids Value:</span>
							<span className="tooltip-metric-value">
								{formatCurrency(
									getFieldValue("totalNonZeroBidsValueInUsd"),
									true,
								)}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">Bid Spend:</span>
							<span className="tooltip-metric-value">
								{formatCurrency(getFieldValue("totalBidSpendInUsd"), true)}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">Publisher Revenue:</span>
							<span className="tooltip-metric-value">
								{formatCurrency(
									getFieldValue("totalCprMarketPublisherRevenueInUsd"),
									true,
								)}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">Partner Spend:</span>
							<span className="tooltip-metric-value">
								{formatCurrency(
									getFieldValue("totalCprMarketDemandPartnerSpendInUsd"),
									true,
								)}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">Deal Volume:</span>
							<span className="tooltip-metric-value">
								{formatLargeNumber(getFieldValue("totalDealVolumeDealInfo"))}
							</span>
						</div>
					</div>

					{/* Win Rates & Efficiency - FIXED with correct field names */}
					<div className="tooltip-section">
						<h5>Win Rates & Efficiency</h5>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">OpenX Win Rate:</span>
							<span className="tooltip-metric-value">
								{formatPercentage(getFieldValue("avgOxWinRate") * 100)}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">Publisher Win Rate:</span>
							<span className="tooltip-metric-value">
								{formatPercentage(getFieldValue("avgPubWinRate") * 100)}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">
								Revenue per Impression:
							</span>
							<span className="tooltip-metric-value">
								{formatCurrency(
									getFieldValue("calculated_revenueperimpression"),
									true,
								)}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">Cost per Impression:</span>
							<span className="tooltip-metric-value">
								{formatCurrency(
									getFieldValue("calculated_costperimpression"),
									true,
								)}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">Bid Guidance:</span>
							<span className="tooltip-metric-value">
								{formatCurrency(getFieldValue("calculated_bid_guidance"), true)}
							</span>
						</div>
					</div>

					{(() => {
						const publisherAccounts = getArrayField("publisheraccountid");
						return publisherAccounts.length > 0 ? (
							<div className="tooltip-section">
								<h5>Publisher Network</h5>
								<div className="tooltip-metric-row">
									<span className="tooltip-metric-label">
										Publisher Accounts:
									</span>
									<span className="tooltip-metric-value">
										{publisherAccounts.length} accounts
										{publisherAccounts.length <= 3
											? `: ${publisherAccounts.join(", ")}`
											: ""}
									</span>
								</div>
							</div>
						) : null;
					})()}

					{/* Additional App Details */}
					{(item.languages ||
						item.keywords ||
						item.features ||
						item.short_description ||
						item.description) && (
						<div className="tooltip-section">
							<h5>Additional Details</h5>
							{item.languages && (
								<div className="tooltip-metric-row">
									<span className="tooltip-metric-label">Languages:</span>
									<span className="tooltip-metric-value">
										{formatArray(getFieldValue(item.languages))}
									</span>
								</div>
							)}
							{item.keywords && (
								<div className="tooltip-metric-row">
									<span className="tooltip-metric-label">Keywords:</span>
									<span className="tooltip-metric-value">
										{formatArray(item.keywords)}
									</span>
								</div>
							)}
							{item.features && (
								<div className="tooltip-metric-row">
									<span className="tooltip-metric-label">Features:</span>
									<span className="tooltip-metric-value">
										{formatArray(item.features)}
									</span>
								</div>
							)}
							{(item.short_description || item.description) && (
								<div className="tooltip-description">
									<strong>Description:</strong>
									<br />
									{item.short_description || item.description}
								</div>
							)}
						</div>
					)}

					{/* App Store Link */}
					{(getAppStoreUrl(item) || item.store_url) && (
						<div className="tooltip-section">
							<a
								href={getAppStoreUrl(item) || item.store_url}
								target="_blank"
								rel="noopener noreferrer"
								className="app-store-link"
							>
								<ExternalLinkIcon /> View in App Store
							</a>
						</div>
					)}
				</div>
			</div>
		);
	};

	// Sort Controls Component
	const SortControls = () => (
		<div className="sort-controls">
			<span className="sort-label">Sort by:</span>
			<select
				value={sortBy}
				onChange={(e) => handleSortByChange(e.target.value)}
				className="sort-select"
			>
				<option value="totalImpressions">Impressions</option>
				<option value="relevance">Relevance</option>
				<option value="marketRequests">Market Requests</option>
				<option value="rating">Rating</option>
				<option value="downloads">Downloads</option>
				<option value="viewability">Viewability</option>
				<option value="ctr">CTR</option>
				<option value="vtr">VTR</option>
				<option value="name">App Name</option>
			</select>

			<button
				onClick={handleSortOrderToggle}
				className="btn btn-secondary btn-small sort-order-btn"
			>
				{sortOrder === "asc" ? "↑ Ascending" : "↓ Descending"}
			</button>
		</div>
	);

	// UPDATED: Table Header Component with new column order
	const TableHeader = () => (
		<div className="results-table-container">
			<table className="results-table">
				<thead>
					<tr>
						<th className="checkbox-column">
							<input
								type="checkbox"
								checked={isAllSelected}
								onChange={handleSelectAll}
								title="Select All"
							/>
						</th>
						<th className="name-column">
							{isSearchVisible ? (
								<div className="search-header-container">
									<input
										type="text"
										placeholder="Search apps..."
										value={searchTerm || ""}
										onChange={handleAppSearch}
										className="app-search-input"
										autoFocus
									/>
									<button
										className="close-search-btn"
										onClick={() => {
											clearSearch();
											setIsSearchVisible(false);
										}}
										title="Close search"
									>
										✖
									</button>
								</div>
							) : (
								<div className="app-header">
									<span className="header-text">App Name</span>
									<button
										className="search-toggle-btn"
										onClick={() => setIsSearchVisible(true)}
										title="Search apps"
									>
										<SearchIcon />
									</button>
								</div>
							)}
						</th>
						<th className="category-column">
							<span className="header-text">Category</span>
						</th>
						<th className="relevance-column">
							<span className="header-text">Relevance</span>
						</th>
						<th className="market-requests-column">
							<span className="header-text">Market Requests</span>
						</th>
						<th className="impressions-column">
							<span className="header-text">Impressions</span>
						</th>
						<th className="viewability-column">
							<span className="header-text">Viewability</span>
						</th>
						<th className="ctr-column">
							<span className="header-text">CTR</span>
						</th>
						<th className="vtr-column">
							<span className="header-text">VTR</span>
						</th>
						<th className="downloads-column">
							<span className="header-text">Downloads</span>
						</th>
						<th className="vtr-column">
							<span className="header-text">Composite score</span>
						</th>
					</tr>
				</thead>
				<tbody>
					{/* Use paginated data instead of raw data */}
					{(() => {
						const paginatedData = getPaginatedData();
						return paginatedData && paginatedData.length > 0 ? (
							paginatedData.map((item, index) => {
								// Create a unique ID for each item - fix duplicate keys
								const itemId = generateUniqueAppKey(item, index, currentPage);
								// Normalize rating to 0-1
								const relevance = calculateRelevance(item); // Debug first 2 items

								return (
									<tr
										key={itemId}
										className={`result-row ${selectedResults.includes(itemId) ? "selected" : ""}`}
									>
										<td className="checkbox-cell">
											<input
												type="checkbox"
												checked={selectedResults.includes(itemId)}
												onChange={() => onResultSelect(itemId)}
											/>
										</td>

										<td
											className={`name-cell ${activeTooltip === itemId ? "tooltip-active" : ""}`}
										>
											<div className="app-name-container">
												<span className="app-name">
													{item.app_name || item.name || "Unknown App"}
												</span>
												<div
													className="info-icon-wrapper"
													onClick={(e) => handleTooltipToggle(itemId, e)}
												>
													<button
														className="info-icon"
														title="View detailed metrics"
													>
														i
													</button>
												</div>
											</div>
											{item.bundle_id && (
												<div className="bundle-id">ID: {item.bundle_id}</div>
											)}
											<div className="app-badges">
												{item.market_source && (
													<div className="market-badge">
														{item.market_source}
													</div>
												)}
											</div>
										</td>

										<td className="category-cell">
											<span className="category-tag">
												{item.category || item.subcategory || "N/A"}
											</span>
										</td>

										<td className="relevance-cell">
											<span
												className={`relevance-value score ${getRelevanceClass(relevance)}`}
											>
												{relevance}%
											</span>
										</td>

										<td className="market-requests-cell">
											<span className="market-requests-value">
												{calculateMarketRequests(item)}
											</span>
										</td>

										<td className="impressions-cell">
											<span className="impressions-value">
												{formatNumber(
													item.totalImpressions || item.impressions,
												)}
											</span>
										</td>

										<td className="viewability-cell">
											<span className="viewability-value">
												{formatPercentage(calculateViewability(item))}
											</span>
										</td>

										<td className="ctr-cell">
											<span className="ctr-value">
												{calculateCTR(item).toFixed(4)}%
											</span>
										</td>

										<td className="vtr-cell">
											<span className="vtr-value">
												{formatPercentage(calculateVTR(item))}
											</span>
										</td>

										<td className="downloads-cell">
											<span className="downloads-value">
												{formatNumber(item.downloads)}
											</span>
										</td>
										<td className="downloads-cell">
											<span className="downloads-value">
												{item._original?.compositeScore
													? (item._original?.compositeScore).toFixed(3)
													: item.compositeScore.toFixed(3)}
											</span>
										</td>
									</tr>
								);
							})
						) : (
							<tr className="empty-results-row">
								<td colSpan={10} className="empty-results-cell">
									<div className="empty-results-content">
										<h3>No Apps Found</h3>
										<p>
											{searchTerm
												? `No results match "${searchTerm}". Try adjusting your search.`
												: "No data available. Try running a search or adjusting your filters."}
										</p>
										{searchTerm && onSearchChange && (
											<button
												onClick={() => onSearchChange("")}
												className="clear-search-button"
											>
												Clear Search
											</button>
										)}
									</div>
								</td>
							</tr>
						);
					})()}
				</tbody>
			</table>
		</div>
	);

	// Loading state
	if (isLoading) {
		return (
			<div className="results-container">
				<div className="results-section">
					<h2 className="section-title">App Results</h2>
					<div className="loading-container">
						<div className="loading-spinner"></div>
						<p>Searching for apps...</p>
					</div>
				</div>
			</div>
		);
	}

	// Error state
	if (error) {
		return (
			<div className="results-container">
				<div className="results-section">
					<h2 className="section-title">App Results</h2>
					<div className="error-container">
						<div className="error-icon">⚠️</div>
						<h3>Search Error</h3>
						<p>{error}</p>
						<button
							className="retry-button"
							onClick={() => window.location.reload()}
						>
							Try Again
						</button>
					</div>
				</div>
			</div>
		);
	}

	// Use calculated values for display
	const displayTotalItems = getCalculatedTotalItems();
	const displayTotalPages = getCalculatedTotalPages();

	return (
		<div className="results-container">
			<div className="results-section">
				<h2 className="section-title">
					App Results
					<span className="results-count">
						({displayTotalItems} apps found)
					</span>
				</h2>

				<TableHeader />

				{/* Pagination - only show if we have pagination data */}
				{displayTotalItems > 0 && displayTotalPages > 1 && (
					<div className="pagination-wrapper">
						<Pagination
							currentPage={currentPage || 1}
							totalPages={displayTotalPages}
							totalItems={displayTotalItems}
							itemsPerPage={itemsPerPage || 20}
							onPageChange={onPageChange}
						/>
					</div>
				)}

				<div className="action-buttons">
					<button
						type="button"
						className="exclude-btn"
						disabled={!selectedResults || selectedResults.length === 0} // ✅ Safe
					>
						Exclude Selected ({selectedResults?.length || 0})
					</button>

					<ExportDropdown />
				</div>
			</div>

			{/* FIXED: Render tooltip in a portal-like container with proper z-index */}
			{activeTooltip && (
				<div className="tooltip-portal">
					<AppMetricsTooltip
						item={getPaginatedData().find((item, index) => {
							const itemId = generateUniqueAppKey(item, index, currentPage);
							return itemId === activeTooltip;
						})}
						isVisible={activeTooltip !== null}
						position={tooltipPosition}
					/>
				</div>
			)}

			<style jsx>{`
                .results-container {
                    width: 100%;
                    max-width: 100%;
                    overflow: hidden;
                    box-sizing: border-box;
                    position: relative;
                }

                .results-section {
                    overflow: visible;
                    position: relative;
                    z-index: 1;
                    width: 100%;
                    max-width: 100%;
                    box-sizing: border-box;
                }

                .section-title {
                    margin: 0 0 10px 0;
                    font-size: 20px;
                    font-weight: 600;
                    color: #1f2937;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    flex-wrap: wrap;
                }

                .results-count {
                    font-size: 14px;
                    color: #6b7280;
                    font-weight: 400;
                }

                .loading-container,
                .error-container {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 60px 20px;
                    text-align: center;
                }

                .loading-spinner {
                    width: 40px;
                    height: 40px;
                    border: 4px solid #f3f4f6;
                    border-top: 4px solid #0076BD;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                    margin-bottom: 20px;
                }

                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }

                .error-icon {
                    font-size: 48px;
                    margin-bottom: 16px;
                }

                .error-container h3 {
                    margin: 0 0 12px 0;
                    font-size: 18px;
                    color: #1f2937;
                }

                .error-container p {
                    margin: 0 0 20px 0;
                    color: #6b7280;
                    max-width: 400px;
                }

                .retry-button {
                    padding: 10px 20px;
                    background: #0076BD;
                    color: white;
                    border: none;
                    border-radius: 6px;
                    font-weight: 500;
                    cursor: pointer;
                    transition: background-color 0.2s;
                }

                .retry-button:hover {
                    background: #005a94;
                }

                .empty-results-row {
                    background-color: white;
                }

                .empty-results-cell {
                    padding: 8px !important;
                    text-align: left;
                    border: none;
                    vertical-align: top;
                }

                .empty-results-content {
                    display: flex;
                    flex-direction: column;
                    color: #666;
                }

                .empty-results-content h3 {
                    margin: 0;
                    font-size: 14px;
                    font-weight: 600;
                    color: black;
                }

                .empty-results-content p {
                    margin: 0;
                    font-size: 12px;
                    color: black;
                    line-height: 1.4;
                }

                .clear-search-button {
                    padding: 8px 16px;
                    background: #0076BD;
                    color: white;
                    border: none;
                    border-radius: 6px;
                    font-size: 14px;
                    font-weight: 500;
                    cursor: pointer;
                    transition: background-color 0.2s;
                }

                .clear-search-button:hover {
                    background: #005a94;
                }

                .results-table-container {
                    overflow-x: auto;
                    overflow-y: visible;
                    position: static;
                    border: 1px solid #dee2e6;
                    border-radius: 8px;
                    background-color: white;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                    margin-bottom: 20px;
                    width: 100%;
                    max-width: 100%;
                    box-sizing: border-box;
                }

                .table-controls {
                    padding: 12px 16px;
                    background: #f8f9fa;
                    border-bottom: 1px solid #dee2e6;
                    border-radius: 8px 8px 0 0;
                }

                .sort-controls {
                    display: flex;
                    gap: 12px;
                    align-items: center;
                    flex-shrink: 0;
                }

                .sort-label {
                    fontSize: 14px;
                    fontWeight: 500;
                    color: #495057;
                    white-space: nowrap;
                }

                .sort-select {
                    padding: 6px 12px;
                    border: 1px solid #ced4da;
                    border-radius: 4px;
                    fontSize: 14px;
                    background-color: white;
                    color: #495057;
                    min-width: 120px;
                    white-space: nowrap;
                }

                .sort-select:focus {
                    outline: none;
                    border-color: #0076BD;
                    box-shadow: 0 0 0 2px rgba(0, 118, 189, 0.1);
                }

                .btn {
                    padding: 6px 12px;
                    border: 1px solid #ced4da;
                    border-radius: 4px;
                    fontSize: 14px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    white-space: nowrap;
                }

                .btn-small {
                    padding: 4px 8px;
                    fontSize: 12px;
                }

                .btn-secondary {
                    background-color: white;
                    color: #495057;
                    border-color: #ced4da;
                }

                .btn-secondary:hover {
                    background-color: #f8f9fa;
                    border-color: #adb5bd;
                }

                .sort-order-btn {
                    white-space: nowrap;
                    min-width: 100px;
                }

                .results-table {
                    position: static;
                    width: 100%;
                    border-collapse: collapse;
                    table-layout: fixed;
                    font-size: 14px;
                    min-width: 900px;
                }

                .results-table thead {
                    position: relative;
                    z-index: 10;
                    background: #f9fafb;
                }

                .results-table th {
                    background-color: #f8f9fa;
                    border-bottom: 2px solid #dee2e6;
                    padding: 12px 8px;
                    text-align: left;
                    font-weight: 600;
                    font-size: 12px;
                    color: #495057;
                    white-space: nowrap;
                    position: static;
                    overflow: hidden;
                }

                .results-table th.sortable {
                    cursor: pointer;
                    user-select: none;
                    transition: background-color 0.2s;
                }

                .results-table th.sortable:hover {
                    background: #f3f4f6;
                }

                .results-table td {
                    padding: 12px 8px;
                    border-bottom: 1px solid #e9ecef;
                    vertical-align: middle;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    position: static;
                    overflow: hidden;
                }

                .result-row {
                    transition: background-color 0.2s ease;
                }

                .result-row:hover {
                    background-color: #f8f9fa;
                }

                .result-row.selected {
                    background-color: #e3f2fd;
                }

                .app-search-input {
                    width: 100%;
                    padding: 3px 20px 3px 6px;
                    border: 1px solid #d1d5db;
                    border-radius: 3px;
                    font-size: 11px;
                    background-color: white;
                    transition: border-color 0.2s;
                    height: 22px;
                    line-height: 1;
                }

                .app-search-input:focus {
                    outline: none;
                    border-color: #0076BD;
                    box-shadow: 0 0 0 2px rgba(0, 118, 189, 0.1);
                }

                .app-name-container {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    position: static;
                    min-width: 0;
                }

                .app-name {
                    font-weight: 500;
                    color: #212529;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    flex: 1;
                }

                .bundle-id {
                    font-size: 11px;
                    color: #6b7280;
                    font-family: monospace;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }

                .app-badges {
                    display: flex;
                    align-items: center;
                    gap: 4px;
                    flex-shrink: 0;
                }

                .market-badge {
                    background: #e0e7ff;
                    color: #3730a3;
                    padding: 2px 6px;
                    border-radius: 4px;
                    font-size: 9px;
                    font-weight: 500;
                    text-transform: uppercase;
                }

                .app-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                }

                .search-toggle-btn {
                    background: none;
                    border: none;
                    cursor: pointer;
                    font-size: 14px;
                    margin-left: 8px;
                    padding: 2px;
                    color: #6b7280;
                    transition: color 0.2s;
                }

                .search-toggle-btn:hover {
                    color: #374151;
                }

                .search-header-container {
                    position: relative;
                    display: flex;
                    align-items: center;
                }

                .close-search-btn {
                    position: absolute;
                    right: 4px;
                    background: none;
                    border: none;
                    color: #6b7280;
                    cursor: pointer;
                    font-size: 14px;
                    padding: 2px;
                }

                .close-search-btn:hover {
                    color: #374151;
                }

                .info-icon-wrapper {
                    position: static;
                    display: inline-flex;
                    flex-shrink: 0;
                }

                .info-icon {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    width: 18px;
                    height: 18px;
                    border-radius: 50%;
                    background-color: #6c757d;
                    color: white;
                    border: none;
                    font-size: 11px;
                    font-weight: bold;
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .info-icon:hover {
                    background-color: #495057;
                    color: white;
                }

                .category-tag {
                    display: inline-block;
                    padding: 3px 8px;
                    background: #f3f4f6;
                    color: #374151;
                    font-size: 11px;
                    border-radius: 4px;
                    border: 1px solid #d1d5db;
                    font-weight: 500;
                }

                .downloads-value,
                .impressions-value,
                .market-requests-value {
                    font-weight: 500;
                    color: #1f2937;
                }

                /* Relevance Score Styling */
                .relevance-value {
                    font-weight: 500;
                    padding: 4px 8px;
                    border-radius: 4px;
                    font-size: 12px;
                    display: inline-block;
                    min-width: 45px;
                    text-align: center;
                }

                .score.excellent {
                    background-color: #d4edda;
                    color: #155724;
                }

                .score.great {
                    background-color: #d1ecf1;
                    color: #0c5460;
                }

                .score.acceptable {
                    background-color: #fff3cd;
                    color: #856404;
                }

                .score.poor {
                    background-color: #f8d7da;
                    color: #721c24;
                }

                .score.irrelevant {
                    background-color: #f5f5f5;
                    color: #6c757d;
                }

                .viewability-value,
                .ctr-value,
                .vtr-value {
                    color: #1f2937;
                    font-weight: 500;
                }

                /* FIXED: Tooltip portal with highest z-index */
                .tooltip-portal {
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    pointer-events: none;
                    z-index: 999999;
                }

                /* FIXED: Improved metrics tooltip styles */
                .metrics-tooltip {
                    background: #0076BD;
                    color: white;
                    border-radius: 8px;
                    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
                    min-width: 350px;
                    max-width: 400px;
                    max-height: 400px;
                    overflow-y: auto;
                    pointer-events: auto;
                    border: 2px solid rgba(255, 255, 255, 0.2);
                }

                .tooltip-header {
                    padding: 12px 16px;
                    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
                    border-radius: 8px 8px 0 0;
                }

                .tooltip-header h4 {
                    margin: 0;
                    font-size: 14px;
                    font-weight: 600;
                    color: white;
                }

                .header-badges {
                    display: flex;
                    gap: 8px;
                    align-items: center;
                    margin-top: 8px;
                    flex-wrap: wrap;
                }

                .rating-badge-tooltip {
                    background: rgba(255, 255, 255, 0.2);
                    padding: 4px 8px;
                    border-radius: 12px;
                    font-size: 11px;
                    font-weight: 500;
                }

                /* Relevance badge in tooltip */
                .relevance-badge-tooltip {
                    padding: 4px 8px;
                    border-radius: 12px;
                    font-size: 11px;
                    font-weight: 500;
                }

                .relevance-badge-tooltip.excellent {
                    background: rgba(212, 237, 218, 0.9);
                    color: #155724;
                }

                .relevance-badge-tooltip.great {
                    background: rgba(209, 236, 241, 0.9);
                    color: #0c5460;
                }

                .relevance-badge-tooltip.acceptable {
                    background: rgba(255, 243, 205, 0.9);
                    color: #856404;
                }

                .relevance-badge-tooltip.poor {
                    background: rgba(248, 215, 218, 0.9);
                    color: #721c24;
                }

                .relevance-badge-tooltip.irrelevant {
                    background: rgba(245, 245, 245, 0.9);
                    color: #6c757d;
                }

                .tooltip-content {
                    padding: 16px;
                }

                .tooltip-section {
                    margin-bottom: 16px;
                }

                .tooltip-section:last-child {
                    margin-bottom: 0;
                }

                .tooltip-section h5 {
                    margin: 0 0 10px 0;
                    font-size: 13px;
                    font-weight: 600;
                    color: white;
                    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
                    padding-bottom: 6px;
                }

                .tooltip-metric-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    padding: 6px 0;
                    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
                    gap: 8px;
                }

                .tooltip-metric-row:last-child {
                    border-bottom: none;
                }

                .tooltip-metric-label {
                    font-weight: 400;
                    color: rgba(255, 255, 255, 0.9);
                    font-size: 12px;
                    flex-shrink: 0;
                    min-width: 120px;
                }

                .tooltip-metric-value {
                    font-weight: 500;
                    color: white;
                    font-size: 12px;
                }

                /* Relevance score styling in tooltip */
                .tooltip-metric-value.score {
                    padding: 2px 6px;
                    border-radius: 4px;
                    font-size: 11px;
                }

                .tooltip-description {
                    color: rgba(255, 255, 255, 0.9);
                    font-size: 12px;
                    line-height: 1.4;
                    max-height: 60px;
                    overflow-y: auto;
                    white-space: normal;
                    word-wrap: break-word;
                }

                .app-store-link {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    color: white;
                    text-decoration: none;
                    font-size: 12px;
                    font-weight: 500;
                    padding: 8px 12px;
                    background: rgba(255, 255, 255, 0.2);
                    border-radius: 6px;
                    transition: background-color 0.2s;
                }

                .app-store-link:hover {
                    background: rgba(255, 255, 255, 0.3);
                }

                /* Pagination wrapper for better responsive handling */
                .pagination-wrapper {
                    width: 100%;
                    max-width: 100%;
                    overflow-x: auto;
                    overflow-y: visible;
                    margin-bottom: 20px;
                }

                .action-buttons {
                    display: flex;
                    gap: 12px;
                    align-items: center;
                    margin-top: 20px;
                    padding: 16px;
                    background-color: #f8f9fa;
                    border-radius: 8px;
                    border: 1px solid #dee2e6;
                    width: 100%;
                    max-width: 100%;
                    box-sizing: border-box;
                    flex-wrap: wrap;
                }

                .btn-medium-secondary {
                    padding: 8px 16px;
                    background-color: #1FBAB0;
                    color: white;
                    border: none;
                    border-radius: 4px;
                    font-weight: 500;
                    cursor: pointer;
                    transition: background-color 0.2s;
                }

                .btn-medium-secondary:hover:not(:disabled) {
                    background-color: #199f98;
                }

                .btn-medium-secondary:disabled {
                    background-color:rgb(24, 176, 179);
                    cursor: not-allowed;
                }

                .exclude-btn {
                    padding: 8px 16px;
                    background-color: #cb2816ff;
                    color: white;
                    border: none;
                    border-radius: 4px;
                    font-weight: 500;
                    cursor: pointer;
                    transition: background-color 0.2s;
                }

                .exclude-btn:hover:not(:disabled) {
                    background-color: #f2a09cff;
                }

                .exclude-btn:disabled {
                    background-color: #f2a09cff;
                    cursor: not-allowed;
                }

                /* Export Dropdown Styles */
                .export-dropdown {
                    position: relative;
                }

                .export-dropdown-btn {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    background-color: #0076BD;
                }

                .export-dropdown-btn:hover:not(:disabled) {
                    background-color: #005a94;
                }

                .dropdown-menu {
                    position: absolute;
                    top: 100%;
                    right: 0;
                    background-color: white;
                    border: 1px solid #dee2e6;
                    border-radius: 4px;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
                    z-index: 1000;
                    min-width: 160px;
                    margin-top: 4px;
                }

                .dropdown-item {
                    width: 100%;
                    padding: 8px 12px;
                    border: none;
                    background: none;
                    text-align: left;
                    cursor: pointer;
                    font-size: 14px;
                    color: #212529;
                    transition: background-color 0.2s;
                }

                .dropdown-item:hover:not(:disabled) {
                    background-color: #f8f9fa;
                }

                .dropdown-item:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                .dropdown-item:first-child {
                    border-top-left-radius: 4px;
                    border-top-right-radius: 4px;
                }

                .dropdown-item:last-child {
                    border-bottom-left-radius: 4px;
                    border-bottom-right-radius: 4px;
                }

                /* UPDATED: Column widths for new layout */
                .checkbox-column { width: 40px; }
                .name-column { min-width: 200px; width: 22%; }
                .category-column { min-width: 80px; width: 12%; }
                .relevance-column { min-width: 80px; width: 9%; }
                .market-requests-column { min-width: 100px; width: 10%; }
                .impressions-column { min-width: 90px; width: 10%; }
                .viewability-column { min-width: 80px; width: 8%; }
                .ctr-column { min-width: 70px; width: 7%; }
                .vtr-column { min-width: 70px; width: 7%; }
                .downloads-column { min-width: 80px; width: 9%; }

                @media (max-width: 1200px) {
                    .metrics-tooltip {
                        min-width: 320px;
                        max-width: 90vw;
                    }

                    .results-table {
                        min-width: 800px;
                    }
                }

                @media (max-width: 768px) {
                    .results-container {
                        margin: 5px;
                        border-radius: 6px;
                    }

                    .section-title {
                        font-size: 16px;
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 5px;
                    }

                    .results-table {
                        min-width: 750px;
                        font-size: 12px;
                    }

                    .results-table th,
                    .results-table td {
                        padding: 6px 4px;
                    }

                    .app-name-container {
                        min-width: 130px;
                    }

                    .app-name {
                        font-size: 12px;
                        max-width: 110px;
                    }

                    .bundle-id {
                        font-size: 9px;
                        max-width: 110px;
                    }

                    .metrics-tooltip {
                        min-width: 90vw;
                        max-width: 90vw;
                        left: 5vw !important;
                        right: auto !important;
                    }

                    .action-buttons {
                        padding: 12px;
                        flex-direction: column;
                        gap: 8px;
                    }

                    .btn-medium-secondary {
                        width: 100%;
                        justify-content: center;
                    }

                    .export-dropdown {
                        width: 100%;
                    }

                    .dropdown-menu {
                        left: 0;
                        right: 0;
                        width: 100%;
                    }

                    .pagination-wrapper {
                        overflow-x: auto;
                        padding-bottom: 5px;
                    }
                }

                @media (max-width: 480px) {
                    .results-table {
                        min-width: 650px;
                        font-size: 11px;
                    }

                    .results-table th,
                    .results-table td {
                        padding: 4px 2px;
                    }

                    .section-title {
                        font-size: 14px;
                    }

                    .results-count {
                        font-size: 12px;
                    }

                    .app-name {
                        font-size: 11px;
                        max-width: 90px;
                    }

                    .bundle-id {
                        font-size: 8px;
                        max-width: 90px;
                    }

                    .category-tag {
                        font-size: 9px;
                        padding: 2px 4px;
                    }

                    .relevance-value {
                        font-size: 10px;
                        padding: 2px 4px;
                        min-width: 35px;
                    }

                    .action-buttons {
                        padding: 8px;
                    }
                }
            `}</style>
		</div>
	);
};
